import type { FeatureMiddleware } from '@cast/api-client';
import type { DbCachingRule } from '@cast/types';

import { getOverride, getOverrideValue } from '../overrides';
import {
  getQueryErrorDetailFixture,
  getQueryErrorQueriesFixture,
  getQueryErrorsFixture,
} from '../fixtures/dbo/errors';
import { DEMO_ENTITY_IDS } from 'features/db-optimization/v2/demo/demoInstances';
import { isDboDemoModeActive } from 'features/db-optimization/v2/demo/demoMode';
import type { ScenarioId } from '../fixtures/dbo/types';

type StoreModule = typeof import('../fixtures/dbo/store');

let storeModulePromise: Promise<StoreModule> | null = null;

const DEFAULT_SCENARIO: ScenarioId = 'empty';

const isScenarioId = (value: string | undefined): value is ScenarioId =>
  value === 'manual' || value === 'aws' || value === 'gcp' || value === 'empty';

const getScenario = (): ScenarioId => {
  const value = getOverrideValue('mockDboScenario');
  return isScenarioId(value) ? value : DEFAULT_SCENARIO;
};

const getStore = async () => {
  if (!getOverride('mockDboDatabaseInstances')) return undefined;
  if (!storeModulePromise) storeModulePromise = import('../fixtures/dbo/store');
  const { ensureStore } = await storeModulePromise;
  return ensureStore(getScenario());
};

/**
 * Demo store serves end users visiting demo instances — no Dev Toolbox
 * override needed. Seeded with the empty scenario so only the demo
 * instances themselves (plus promoted demos) are ever served from it.
 */
const getDemoStore = async () => {
  if (!storeModulePromise) storeModulePromise = import('../fixtures/dbo/store');
  const { ensureStore } = await storeModulePromise;
  return ensureStore(DEFAULT_SCENARIO);
};

const isDemoEntityId = (value: unknown): boolean =>
  typeof value === 'string' && DEMO_ENTITY_IDS.has(value);

const containsDemoEntityId = (value: unknown, depth = 0): boolean => {
  if (typeof value === 'string') return isDemoEntityId(value);
  if (depth > 3 || value === null || typeof value !== 'object') return false;
  return Object.values(value).some((v) => containsDemoEntityId(v, depth + 1));
};

/**
 * Dev override wins — the full mock world serves every request. Without
 * it, only requests that reference a demo entity id (instance, database,
 * proxy, cache config) are served from the demo store; everything else
 * reaches the real API.
 */
const getStoreForRequest = async (args: unknown) => {
  const store = await getStore();
  if (store) return store;
  return containsDemoEntityId(args) ? getDemoStore() : undefined;
};

/**
 * Org-level overview candidate endpoints carry no entity id to gate on, so
 * demo mode is the explicit opt-in that switches them to the demo dataset.
 * The Dev override keeps priority and serves the scenario data.
 */
const getOverviewCandidatesContext = async (): Promise<{
  store: Awaited<ReturnType<typeof getDemoStore>>;
  demo: boolean;
} | null> => {
  const store = await getStore();
  if (store) return { store, demo: false };
  if (isDboDemoModeActive()) {
    return { store: await getDemoStore(), demo: true };
  }
  return null;
};

const now = () => new Date().toISOString();

const DEMO_REGISTRATION_SCRIPT = [
  'helm upgrade --install dbo-proxy castai/dbo-proxy \\',
  '  --namespace castai-dbo \\',
  '  --create-namespace \\',
  '  --set castai.apiKey=$CASTAI_API_KEY \\',
  '  --set databaseInstance.endpoint=<your-database-endpoint>',
].join('\n');

type TimeParams = {
  startTime?: string;
  endTime?: string;
  stepSeconds?: number;
};

const getTimeParams = (arg: TimeParams | undefined, defaultStep: number) => {
  const { startTime, endTime, stepSeconds } = arg ?? {};
  return {
    startTime: startTime ?? now(),
    endTime: endTime ?? now(),
    stepSeconds: stepSeconds ?? defaultStep,
  };
};

export const dbo: FeatureMiddleware<'dbo'> = {
  // ── Instances ──
  getDatabaseInstances: async ({ next, resolve }) => {
    const store = await getStore();
    if (store) return resolve(store.getInstances());
    // Demo mode serves the demo dataset (flagship + discovered instances)
    // so end users can explore the overview without the Dev Toolbox.
    if (isDboDemoModeActive()) {
      const demoStore = await getDemoStore();
      return resolve(demoStore.getDemoInstances());
    }
    return next();
  },

  getDatabaseInstanceDetails: async ({ next, resolve, reject, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const id = args?.[0]?.id ?? args?.[0];
    // Unknown ids resolve to undefined — the store falls back to demo
    // instances and promotes them into the instance list on demand.
    const details = store.getInstanceDetails(id);
    return details
      ? resolve(details)
      : reject(new Error(`Database instance not found: ${String(id)}`));
  },

  createDatabaseInstance: async ({ next, resolve, args }) => {
    const store = await getStore();
    if (!store) return next();
    return resolve(store.createDatabaseInstance(args?.[0]?.databaseInstance));
  },

  updateDatabaseInstance: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const { id, updateDatabaseInstanceRequest } = args?.[0] ?? {};
    return resolve(
      store.updateDatabaseInstance(id, updateDatabaseInstanceRequest)
    );
  },

  deleteDatabaseInstance: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    store.deleteDatabaseInstance(args?.[0]?.id);
    return resolve();
  },

  createDatabase: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const { databaseInstanceId, database } = args?.[0] ?? {};
    return resolve(store.createDatabase(databaseInstanceId, database));
  },

  deleteDatabase: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const { databaseInstanceId, id } = args?.[0] ?? {};
    store.deleteDatabase(databaseInstanceId, id);
    return resolve();
  },

  getCloudSyncStatus: async ({ next, resolve }) => {
    const store = await getStore();
    return store ? resolve(store.getCloudSyncStatus()) : next();
  },

  getCloudIntegrationSummary: async ({ next, resolve }) => {
    const store = await getStore();
    return store ? resolve(store.getCloudIntegrationSummary()) : next();
  },

  // ── Advisor ──
  getPerformanceAdvisorCacheGroups: async ({ next, resolve }) => {
    const store = await getStore();
    return store ? resolve(store.getAdvisorCacheGroups()) : next();
  },

  getPerformanceAdvisorDatabases: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    return store ? resolve(store.getAdvisorDatabases()) : next();
  },

  getPerformanceAdvisorRecommendations: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const { databaseId, kinds, categories, impacts, statuses } =
      args?.[0] ?? {};
    return resolve(
      store.getAdvisorRecommendations(
        { kinds, categories, impacts, statuses },
        databaseId
      )
    );
  },

  getPerformanceAdvisorRecommendation: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const { databaseId, id } = args?.[0] ?? {};
    return resolve(store.getAdvisorRecommendationDetail(databaseId, id));
  },

  closePerformanceRecommendation: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    store.closePerformanceRecommendation(args?.[0]?.id ?? '');
    return resolve({});
  },

  reopenPerformanceRecommendation: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    store.reopenPerformanceRecommendation(args?.[0]?.id ?? '');
    return resolve({});
  },

  // ── Overview candidates ──
  listCloudOptimizationCandidates: async ({ next, resolve }) => {
    const ctx = await getOverviewCandidatesContext();
    return ctx
      ? resolve(ctx.store.getOverviewCandidates({ demo: ctx.demo }))
      : next();
  },

  listTopQueryRecommendations: async ({ next, resolve }) => {
    const ctx = await getOverviewCandidatesContext();
    return ctx
      ? resolve(ctx.store.getTopQueryRecommendations({ demo: ctx.demo }))
      : next();
  },

  listCachingPotentialDatabases: async ({ next, resolve }) => {
    const ctx = await getOverviewCandidatesContext();
    return ctx
      ? resolve(ctx.store.getCachingPotentialDatabases({ demo: ctx.demo }))
      : next();
  },

  listConnectionPoolingCandidates: async ({ next, resolve }) => {
    const ctx = await getOverviewCandidatesContext();
    return ctx
      ? resolve(ctx.store.getConnectionPoolingCandidates({ demo: ctx.demo }))
      : next();
  },

  // ── Metrics ──
  getInfrastructureMetrics: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const p = getTimeParams(args[0], 3600);
    return resolve(
      store.getInfrastructureMetrics(p.startTime, p.endTime, p.stepSeconds)
    );
  },

  getHealthMetrics: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const p = getTimeParams(args[0], 60);
    return resolve(
      store.getHealthMetrics({
        startTime: p.startTime,
        endTime: p.endTime,
        stepSeconds: p.stepSeconds,
        nodeId: args[0]?.nodeId,
      })
    );
  },

  getDatabaseCacheEfficiency: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const p = getTimeParams(args[0], 3600);
    return resolve(
      store.getDatabaseCacheEfficiency(p.startTime, p.endTime, p.stepSeconds)
    );
  },

  getCacheGroupPerformanceMetrics: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    return store ? resolve(store.getCacheGroupPerformanceMetrics()) : next();
  },

  getCacheGroupOperationalMetrics: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    return store ? resolve(store.getCacheGroupOperationalMetrics()) : next();
  },

  getCacheGroupMetricsFilterOptions: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    return store ? resolve(store.getCacheGroupMetricsFilterOptions()) : next();
  },

  // ── Caching ──
  getCacheGroup: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    return store ? resolve(store.getCacheGroup()) : next();
  },

  getDatabaseCachingSummary: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    return store ? resolve(store.getDatabaseCachingSummary()) : next();
  },

  getDatabaseInstanceCachingSummary: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const { databaseInstanceId } = args?.[0] ?? {};
    return resolve(store.getDatabaseInstanceCachingSummary(databaseInstanceId));
  },

  getDatabaseInstanceMetricsFilterOptions: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    return store
      ? resolve(store.getDatabaseInstanceMetricsFilterOptions())
      : next();
  },

  getCachingRules: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const { groupId, cacheId } = args?.[0] ?? {};
    return resolve(store.getCachingRules(groupId ?? '', cacheId ?? ''));
  },

  createCachingRule: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const { groupId, id: cacheId, config } = args?.[0] ?? {};
    const newRule: DbCachingRule = config ?? {
      id: `rule-${Date.now()}`,
      table: 'new_table',
      mode: 'Auto',
    };
    return resolve(
      store.createCachingRule(groupId ?? '', cacheId ?? '', newRule)
    );
  },

  updateCachingRule: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const { groupId, cacheId, config } = args?.[0] ?? {};
    const rule: DbCachingRule = config ?? { id: '', table: '', mode: 'Auto' };
    return resolve(store.updateCachingRule(groupId ?? '', cacheId ?? '', rule));
  },

  deleteCachingRule: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const { groupId, cacheId, id } = args?.[0] ?? {};
    store.deleteCachingRule(groupId ?? '', cacheId ?? '', id ?? '');
    return resolve({});
  },

  listCacheRules: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const { proxyId, databaseName, templateHash, table } = args?.[0] ?? {};
    const { items } = store.listCacheRules(proxyId ?? '', databaseName ?? '');
    const templateSql = templateHash
      ? store.getProxyQueryTemplate(templateHash).template
      : undefined;
    const templateIdentifiers = new Set(
      (templateSql ?? '').toLowerCase().split(/[^\w$#]+/)
    );

    const touchesTable = (name?: string) =>
      !!name && templateIdentifiers.has(name.toLowerCase());
    const filtered = items.filter((rule) => {
      if (templateHash)
        return rule.templateHash === templateHash || touchesTable(rule.table);
      if (table) {
        const tablePattern = new RegExp(`\\b${table}\\b`);
        return rule.table === table || tablePattern.test(rule.template ?? '');
      }
      return true;
    });
    return resolve({ items: filtered });
  },

  createCacheRule: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const { proxyId, databaseName, cacheRule } = args?.[0] ?? {};
    return resolve(
      store.createCacheRule(
        proxyId ?? '',
        databaseName ?? '',
        cacheRule ?? undefined
      )
    );
  },

  updateCacheRule: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const { proxyId, databaseName, cacheRule } = args?.[0] ?? {};
    return resolve(
      store.updateCacheRule(
        proxyId ?? '',
        databaseName ?? '',
        cacheRule ?? undefined
      )
    );
  },

  deleteCacheRule: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const { proxyId, databaseName, id } = args?.[0] ?? {};
    store.deleteCacheRule(proxyId ?? '', databaseName ?? '', id ?? '');
    return resolve();
  },

  createProxy: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    return resolve(store.createProxy(args?.[0]?.proxy));
  },

  updateProxyDatabase: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    return resolve(
      store.updateProxyDatabase(args?.[0]?.proxyDatabaseUpdate?.cacheMode)
    );
  },

  // ── Proxy metrics (v2) ──
  getProxyQueriesV2: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const { pageLimit, pageCursor, queryContains } = args?.[0] ?? {};
    return resolve(
      store.getProxyQueries({ pageLimit, pageCursor, queryContains })
    );
  },

  getCacheQueryInsightsV2: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const { templateHash, startTime, endTime, stepSeconds } = args?.[0] ?? {};
    return resolve(
      store.getProxyQueryInsights({
        templateHash,
        startTime,
        endTime,
        stepSeconds,
      })
    );
  },

  getProxyCacheQueryTemplate: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const { templateHash } = args?.[0] ?? {};
    return resolve(store.getProxyQueryTemplate(templateHash ?? ''));
  },

  getProxyCacheSummary: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    return store ? resolve(store.getDatabaseCachingSummary()) : next();
  },

  getInstanceProxyCacheMetrics: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const p = getTimeParams(args[0], 3600);
    return resolve(
      store.getInstanceProxyCacheMetrics(p.startTime, p.endTime, p.stepSeconds)
    );
  },

  getProxyConnectionsMetrics: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const p = getTimeParams(args[0], 60);
    return resolve(
      store.getProxyConnectionsMetrics(p.startTime, p.endTime, p.stepSeconds)
    );
  },

  getProxyPerformanceMetrics: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const p = getTimeParams(args[0], 60);
    return resolve(
      store.getProxyPerformanceMetrics(p.startTime, p.endTime, p.stepSeconds)
    );
  },

  getInstanceTelemetry: async ({ next, resolve }) => {
    const store = await getStore();
    if (!store) return next();
    return resolve(store.getInstancesTelemetry());
  },

  // ── Upstream query errors (TEMPORARY: POLY-2202 pre-API mocks) ──
  getQueryErrors: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const { instanceId, startTime, endTime, database } = args?.[0] ?? {};
    return resolve(
      getQueryErrorsFixture({ instanceId, startTime, endTime, database })
    );
  },

  getQueryErrorDetail: async ({ next, resolve, reject, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const { instanceId, errorId } = args?.[0] ?? {};
    const detail = getQueryErrorDetailFixture({ instanceId, errorId });
    return detail
      ? resolve(detail)
      : reject(new Error(`Query error not found: ${String(errorId)}`));
  },

  getQueryErrorQueries: async ({ next, resolve, reject, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const { instanceId, errorId, startTime, endTime } = args?.[0] ?? {};
    const queries = getQueryErrorQueriesFixture({
      instanceId,
      errorId,
      startTime,
      endTime,
    });
    return queries
      ? resolve(queries)
      : reject(new Error(`Query error not found: ${String(errorId)}`));
  },

  // ── Registration script (v2 enable-optimization dialog) ──
  createRegistration: async ({ next, resolve, args }) => {
    const store = await getStoreForRequest(args);
    if (!store) return next();
    const registration = args?.[0];
    return resolve({
      ...registration,
      id: 'demo-registration',
      executeCommand: DEMO_REGISTRATION_SCRIPT,
    });
  },
};
